const DEFAULT_ERROR_MESSAGE_SELECTOR = '[data-ui-el="default-error-message"]'
const DEFAULT_SUPPORT_MESSAGE_SELECTOR = '[data-ui-el="default-support-message"]'

const FORM_ELEMENT_VALID_CLASS = 'nfb-form__element-container--valid'
const FORM_ELEMENT_INVALID_CLASS = 'nfb-form__element-container--invalid'
const INPUT_ELEMENT_VALID_CLASS = 'nfb-form__text-container--simple--valid'
const INPUT_ELEMENT_INVALID_CLASS = 'nfb-form__text-container--simple--invalid'
const FILE_PREVIEW_VALID_CLASS = 'nfb-form__file-preview-container--valid'
const FILE_PREVIEW_INVALID_CLASS = 'nfb-form__file-preview-container--invvalid'

const FORM_ERROR_MESSAGE_CLASS = 'nfb-form__message--error'
const DYNAMIC_ERROR_MESSAGE_ID = 'dynamic-error-message'

export enum FormElementStatus {
  VALID = 'valid',
  INVALID = 'invalid',
  NEUTRAL = 'neutral'
}

// Values: badInput, customError, patternMismatch, rangeOverflow
// rangeUnderflow, stepMismatch, tooLong, tooShort, typeMismatch
// valid, valueMissing
export type ErrorMessageKey = keyof ValidityState

/**
 *
 * @param formElementContainer
 * @param input
 * @returns An HTMLElement corresponding to a selector built from the error
 * defined by input.validty, or null if no element found or no error.
 */
const getErrorMessageElementForInputValdity = (
  formElementContainer: HTMLElement,
  input: HTMLInputElement | HTMLTextAreaElement
): HTMLElement | null => {
  for (const key in input.validity) {
    if (key !== 'valid' && input.validity[key as ErrorMessageKey]) {
      return formElementContainer.querySelector(`[data-ui-el="error-message-${key}"]`)
    }
  }

  return null
}

/**
 *
 * @param input An HTMLInputElement or HTMLTextAreaElement
 * @returns Whether the input is valid according to checkValidity()
 * Checks if an input is valid according to checkValidity. Toggle form
 * element error according to result.
 */
export const validateInput = (input: HTMLInputElement | HTMLTextAreaElement): boolean => {
  const formElementContainer = input.closest('.nfb-form__element-container') as HTMLElement
  const status = input.checkValidity() ? FormElementStatus.VALID : FormElementStatus.INVALID
  const errorMessageElement = getErrorMessageElementForInputValdity(formElementContainer, input)
  toggleFormElementStatus(formElementContainer, status, errorMessageElement)
  return status === FormElementStatus.VALID
}

/**
 *
 * @param select An HTMLElement that is the root of a Select template.
 * @returns True if there is a value selected and it is included in the list of options,
 * false if there is no value or if the value is not included in the list. If the input
 * is not marked as required, validate if there is a value and it is included in the list,
 * return true for empty value.
 */
export const validateSelect = (select: HTMLElement): boolean => {
  let isValid = false
  const formElementContainer = select.classList.contains('nfb-form__element-container')
    ? select
    : select.closest('.nfb-form__element-container') as HTMLElement

  const hiddenInput = select.querySelector('[data-ui-el="nfb-form-selected-option"]') as HTMLInputElement
  const visibleInput = select.querySelector('[role="combobox"]') as HTMLInputElement

  if (hiddenInput && visibleInput) {
    const valuesElementList = select.querySelector('[role="listbox"]')?.querySelectorAll('*')
    if (valuesElementList?.length) {
      const value = hiddenInput.value
      const valuesList = Array.from(valuesElementList).map(i => (i as HTMLElement).dataset.value)
      const isRequired = visibleInput.hasAttribute('required')
      isValid = isRequired ? !!(value) && valuesList.includes(value) : !value || valuesList.includes(value)
      const status = isValid ? FormElementStatus.VALID : FormElementStatus.INVALID
      const errorMessageElement = getErrorMessageElementForInputValdity(formElementContainer, visibleInput)
      toggleFormElementStatus(formElementContainer, status, errorMessageElement)
    }
  }
  return isValid
}

/**
 *
 * @param formElement
 * @param status Neutral, valid or invalid.
 * @param errorMessage HTMLElement if error message is an existing element in the template,
 * String if it is a dynamic message that needs to be added to the template,
 * null if the message with the selector [data-ui-el="default-error-message"] should be used
 * as an error message.
 *
 * Sets the appropriate classes through an nfb-form__element-container based template
 * according to status neutral, invalid or valid.
 * Displays an error message if any when invalid, a support message if any when valid, hides
 * all messages if neutral.
 */
export const toggleFormElementStatus = (
  formElement: HTMLElement,
  status: FormElementStatus,
  errorMessage: HTMLElement | string | null = null
): void => {
  const inputElement =
    formElement.querySelector('.nfb-form__text-container--simple, .nfb-form__text-container--multiple')

  const fileInputPreview = formElement.querySelector('[data-ui-el="nfb-form-file-preview"]')

  const statusIsValid = status === FormElementStatus.VALID
  const statusIsInvalid = status === FormElementStatus.INVALID
  formElement.classList.toggle(FORM_ELEMENT_VALID_CLASS, statusIsValid)
  formElement.classList.toggle(FORM_ELEMENT_INVALID_CLASS, statusIsInvalid)

  if (inputElement) {
    inputElement.classList.toggle(INPUT_ELEMENT_VALID_CLASS, statusIsValid)
    inputElement.classList.toggle(INPUT_ELEMENT_INVALID_CLASS, statusIsInvalid)
  }

  if (fileInputPreview) {
    fileInputPreview.classList.toggle(FILE_PREVIEW_VALID_CLASS, statusIsValid)
    fileInputPreview.classList.toggle(FILE_PREVIEW_INVALID_CLASS, statusIsInvalid)
  }

  const messageContainer = formElement.querySelector('.nfb-form__message')
  if (messageContainer) {
    // Hide all message by default
    messageContainer.querySelectorAll(':not(i)').forEach((e: Node) => {
      const eHtmlElement = e as HTMLElement
      if (eHtmlElement.dataset.uiEl === DYNAMIC_ERROR_MESSAGE_ID) {
        eHtmlElement.remove()
      } else {
        eHtmlElement.dataset.uiShow = false.toString()
      }
    })

    if (status === FormElementStatus.INVALID) {
      if (errorMessage) {
        let errorElement = errorMessage
        if (typeof errorMessage === 'string') {
          // String error message: add message dynamically
          errorElement = document.createElement('span')
          errorElement.dataset.uiEl = DYNAMIC_ERROR_MESSAGE_ID
          errorElement.classList.add(FORM_ERROR_MESSAGE_CLASS)
          errorElement.textContent = errorMessage
          messageContainer.appendChild(errorElement as Node)
        } else {
          // Element error message: find and show
          errorMessage.dataset.uiShow = true.toString()
        }
      } else {
        // Invalid but no error message: show default error message if found
        const defaultErrorMessage = messageContainer.querySelector(DEFAULT_ERROR_MESSAGE_SELECTOR) as HTMLElement
        if (defaultErrorMessage) {
          defaultErrorMessage.dataset.uiShow = true.toString()
        }
      }
    } else if (status === FormElementStatus.VALID) {
      // Valid: show default support message if found
      const defaultSupportMessage = messageContainer.querySelector(DEFAULT_SUPPORT_MESSAGE_SELECTOR) as HTMLElement
      if (defaultSupportMessage) {
        defaultSupportMessage.dataset.uiShow = true.toString()
      }
    }
  }
}
